import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import { 
  Eye, 
  Download, 
  Copy, 
  Clock, 
  FileText, 
  Image, 
  Target, 
  TrendingUp,
  X,
  Check,
  Star,
  Calendar,
  ExternalLink,
  ArrowLeft,
  BarChart3,
  Info,
  ChevronDown,
  FileCode,
  Type,
  Hash,
  Store,
  Globe,
  Loader2,
  AlertCircle
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { ArticlePreviewSkeleton } from './ArticlePreviewSkeleton'
import { sanitizeArticleHtml, getArticleIsolationStyles, getArticleScopeClass } from '@/utils/sanitizeArticleHtml'
import { PLATFORM_IMAGES } from '@/constants/platformImages'
import { useAdmin } from '@/hooks/useAdmin'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"

interface OptimizedArticlePreviewProps {
  articleId: string
  onClose: () => void
  onBack?: () => void
}

interface GeneratedArticle {
  id: string
  title: string
  content: string
  html_content: string
  meta_title: string
  meta_description: string
  primary_keyword: string
  secondary_keywords: string[]
  word_count: number
  reading_time: number
  featured_image_url?: string
  supporting_images: any[]
  image_prompts: string[]
  generation_model: string
  image_generation_model: string
  created_at: string
  completed_at: string
  generation_status: string
  progress: number
  published_status?: string
  published_platform?: string
  published_url?: string
  published_at?: string
}

export function OptimizedArticlePreview({ articleId, onClose, onBack }: OptimizedArticlePreviewProps) {
  const [article, setArticle] = useState<GeneratedArticle | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishPlatform, setPublishPlatform] = useState<string | null>(null)
  const [platformConnections, setPlatformConnections] = useState<any>({})
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const [showWebflowQueuedDialog, setShowWebflowQueuedDialog] = useState(false)
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null)
  const { isAdmin } = useAdmin()
  const [showAdminWebsiteDialog, setShowAdminWebsiteDialog] = useState(false)
  const [adminWebsiteSettings, setAdminWebsiteSettings] = useState<any>(null)

  useEffect(() => {
    loadArticle()
    loadPlatformConnections()
    if (isAdmin) {
      loadAdminWebsiteSettings()
    }
  }, [articleId, isAdmin])

  const loadPlatformConnections = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: settings } = await supabase
        .from('user_settings')
        .select('platform_connections')
        .eq('user_id', user.id)
        .single()

      if (settings?.platform_connections) {
        setPlatformConnections(settings.platform_connections)
      }
    } catch (error) {
      console.error('Error loading platform connections:', error)
    }
  }

  const loadAdminWebsiteSettings = async () => {
    // No need to load settings for platform blog - it's always available for admins
    setAdminWebsiteSettings({ configured: true })
  }

  const loadArticle = async () => {
    try {
      setIsLoading(true)
      
      // Add a small delay to show skeleton loading for better UX
      await new Promise(resolve => setTimeout(resolve, 300))
      
      const { data, error } = await supabase
        .from('generated_articles')
        .select('*')
        .eq('id', articleId)
        .single()

      if (error) throw error
      setArticle(data)
    } catch (error) {
      console.error('Error loading article:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(type)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (error) {
      // Silent fail
    }
  }

  const copyAsPlainText = () => {
    if (!article) return
    const plainText = article.content || article.html_content?.replace(/<[^>]*>/g, '') || ''
    copyToClipboard(plainText, 'Plain Text')
  }

  const copyAsMarkdown = () => {
    if (!article) return
    // Simple HTML to Markdown conversion
    let markdown = article.html_content || ''
    markdown = markdown
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
      .replace(/<[^>]*>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
    
    copyToClipboard(markdown, 'Markdown')
  }

  const getConnectedPlatforms = () => {
    const connected: string[] = []
    if (platformConnections.wordpress?.connected) connected.push('wordpress')
    if (platformConnections.shopify?.connected) connected.push('shopify')
    if (platformConnections.wix?.connected) connected.push('wix')
    if (platformConnections.webflow?.connected) connected.push('webflow')
    return connected
  }

  const handlePublish = async (platform: string) => {
    if (!article) return

    // For admin website (platform blog), no configuration needed

    setIsPublishing(true)
    setPublishPlatform(platform)
    setShowPublishDialog(false)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        toast.error('You must be logged in to publish')
        setIsPublishing(false)
        setPublishPlatform(null)
        return
      }

      // Get website ID if available
      const { data: website } = await supabase
        .from('websites')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_primary', true)
        .single()

      const { data, error } = await supabase.functions.invoke('publish-article', {
        body: {
          articleId: article.id,
          platform,
          websiteId: website?.id
        }
      })

      if (error) throw error

      if (data.success) {
        // For Webflow, show special dialog explaining queued status
        if (data.queued && platform === 'webflow') {
          setPublishedUrl(data.publishedUrl)
          setShowWebflowQueuedDialog(true)
          // Update article status without reloading to prevent flashing
          if (article) {
            setArticle({
              ...article,
              published_status: 'publishing',
              published_url: data.publishedUrl,
              published_platform: platform
            })
          }
        } else {
          toast.success(`Article published successfully to ${platform === 'admin-website' ? 'your website' : platform}!`)
          // Smoothly update article status without full reload
          if (article) {
            setArticle({
              ...article,
              published_status: 'published',
              published_url: data.publishedUrl,
              published_at: new Date().toISOString(),
              published_platform: platform
            })
          }
          // Reload article in background after a delay to prevent flashing
          setTimeout(() => {
            loadArticle()
          }, 500)
        }
      } else if (data.requiresConnection) {
        toast.error(`${platform.charAt(0).toUpperCase() + platform.slice(1)} not connected. Please connect in Settings.`, {
          action: {
            label: 'Go to Settings',
            onClick: () => {
              window.location.href = '/dashboard?tab=settings'
            }
          }
        })
      } else {
        throw new Error(data.error || 'Failed to publish article')
      }
    } catch (error: any) {
      console.error('Error publishing article:', error)
      toast.error(error.message || 'Failed to publish article')
    } finally {
      setIsPublishing(false)
      setPublishPlatform(null)
    }
  }

  const downloadArticle = (format: 'html' | 'text') => {
    if (!article) return

    let content = ''
    let filename = ''
    let mimeType = ''

    if (format === 'html') {
      content = article.html_content || ''
      filename = `${(article.title || 'untitled').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`
      mimeType = 'text/html'
    } else {
      content = article.content || ''
      filename = `${(article.title || 'untitled').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`
      mimeType = 'text/plain'
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const getContentScore = () => {
    if (!article) return 0
    
    let score = 0
    
    // Word count scoring (target: 2000 words)
    if (article.word_count >= 1500) score += 25
    else if (article.word_count >= 1000) score += 15
    else if (article.word_count >= 500) score += 10
    
    // SEO elements
    if (article.meta_title && article.meta_title.length > 0) score += 15
    if (article.meta_description && article.meta_description.length > 0) score += 15
    if (article.primary_keyword) score += 10
    if (article.secondary_keywords && article.secondary_keywords.length > 0) score += 10
    
    // Images
    if (article.supporting_images && article.supporting_images.length > 0) score += 15
    if (article.featured_image_url) score += 10
    
    return Math.min(score, 100)
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50'
    if (score >= 60) return 'text-yellow-600 bg-yellow-50'
    return 'text-red-600 bg-red-50'
  }

  // Calculate reading time consistently from word count (200 words per minute)
  const calculateReadingTime = () => {
    if (!article || !article.word_count) return 0
    return Math.ceil(article.word_count / 200)
  }

  // Show skeleton loading while loading
  if (isLoading) {
    return <ArticlePreviewSkeleton />
  }

  if (!article) {
    return (
      <div className="flex items-center justify-center h-full min-h-[600px]">
        <div className="text-center">
          <p className="text-muted-foreground text-lg mb-4">Article not found</p>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    )
  }

  const contentScore = getContentScore()

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Enhanced Header with gradient styling */}
          <div className="bg-gradient-to-r from-primary/8 via-primary/5 to-primary/12 border-b border-primary/20">
            <div className="w-full px-6 py-4">
              <div className="flex items-center justify-between">
                {/* Left Side - Navigation */}
                <div className="flex items-center">
                  {onBack && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onBack}
                      className="gap-2 rounded-xl border-primary/20 text-primary hover:bg-primary/10 hover:border-primary/30 transition-all duration-300 shadow-sm hover:shadow-md px-4 py-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back to Content
                    </Button>
                  )}
                </div>

                {/* Center - Title */}
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">Article Preview</h2>
                </div>

                {/* Right Side - Actions */}
                <div className="flex items-center gap-2">
              {/* Publish Button */}
              <DropdownMenu open={showPublishDialog} onOpenChange={setShowPublishDialog}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    disabled={isPublishing || article.published_status === 'publishing'}
                    className="gap-2 rounded-lg bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-sm px-3 py-2 disabled:opacity-50"
                  >
                    {isPublishing || article.published_status === 'publishing' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Publishing...
                      </>
                    ) : article.published_status === 'published' ? (
                      <>
                        <Check className="h-4 w-4" />
                        Published
                      </>
                    ) : (
                      <>
                        <ExternalLink className="h-4 w-4" />
                        Publish
                      </>
                    )}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl border border-border shadow-lg bg-background min-w-[200px]">
                  {getConnectedPlatforms().length > 0 || isAdmin ? (
                    <>
                      {getConnectedPlatforms().length > 0 && (
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                          Publish to
                        </div>
                      )}
                      {platformConnections.wordpress?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('wordpress')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.wordpress} alt="WordPress" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">WordPress</div>
                            <div className="text-xs text-muted-foreground">{platformConnections.wordpress.siteUrl}</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {platformConnections.shopify?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('shopify')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.shopify} alt="Shopify" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Shopify</div>
                            <div className="text-xs text-muted-foreground">{platformConnections.shopify.domain || platformConnections.shopify.storeUrl}</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {platformConnections.wix?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('wix')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.wix} alt="Wix" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Wix</div>
                            <div className="text-xs text-muted-foreground">Site ID: {platformConnections.wix.siteId}</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {platformConnections.webflow?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('webflow')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.webflow} alt="Webflow" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Webflow</div>
                            <div className="text-xs text-muted-foreground">CMS Publishing</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {platformConnections.framer?.connected && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish('framer')}
                          disabled={isPublishing}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                        >
                          <div className="p-1.5 rounded-lg bg-background border border-border/30">
                            <img src={PLATFORM_IMAGES.framer} alt="Framer" className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Framer</div>
                            <div className="text-xs text-muted-foreground">CMS Publishing</div>
                          </div>
                        </DropdownMenuItem>
                      )}
                      {isAdmin && (
                        <>
                          {getConnectedPlatforms().length > 0 && <Separator className="my-1" />}
                          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                            Admin Only
                          </div>
                          <DropdownMenuItem 
                            onClick={() => handlePublish('admin-website')}
                            disabled={isPublishing}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                          >
                            <div className="p-1.5 rounded-lg bg-primary/10">
                              <Globe className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-foreground">Publish to Platform Blog</div>
                              <div className="text-xs text-muted-foreground">
                                seoro.so/blog (SEO optimized)
                              </div>
                            </div>
                          </DropdownMenuItem>
                        </>
                      )}
                      {getConnectedPlatforms().length > 0 && (
                        <>
                          <Separator className="my-1" />
                          <DropdownMenuItem 
                            onClick={() => {
                              setShowPublishDialog(false)
                              window.location.href = '/dashboard?tab=settings'
                            }}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                          >
                            <div className="p-1.5 rounded-lg bg-primary/10">
                              <ExternalLink className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium text-foreground">Manage Connections</div>
                              <div className="text-xs text-muted-foreground">Add or remove platforms</div>
                            </div>
                          </DropdownMenuItem>
                        </>
                      )}
                    </>
                  ) : !isAdmin ? (
                    <>
                      <div className="px-4 py-3 text-center">
                        <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <div className="font-medium text-foreground mb-1">No Platforms Connected</div>
                        <div className="text-xs text-muted-foreground mb-3">
                          Connect a platform in Settings to publish articles
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            setShowPublishDialog(false)
                            window.location.href = '/dashboard?tab=settings'
                          }}
                          className="w-full bg-gradient-to-r from-primary via-primary to-primary/90"
                        >
                          Go to Settings
                        </Button>
                      </div>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 rounded-xl border-primary/20 text-primary hover:bg-primary/10 hover:border-primary/30 transition-all duration-300 shadow-sm hover:shadow-md px-3 py-2"
                  >
                    {copiedField ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copiedField ? 'Copied!' : 'Copy'}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl border border-border shadow-lg bg-background">
                  <DropdownMenuItem 
                    onClick={() => copyToClipboard(article.html_content || '', 'HTML')}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                  >
                    <div className="p-1.5 rounded-lg bg-primary/10">
                      <FileCode className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Copy as HTML</div>
                      <div className="text-xs text-muted-foreground">Raw HTML code</div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={copyAsPlainText}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                  >
                    <div className="p-1.5 rounded-lg bg-primary/10">
                      <Type className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Copy as Plain Text</div>
                      <div className="text-xs text-muted-foreground">Clean text only</div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={copyAsMarkdown}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-200"
                  >
                    <div className="p-1.5 rounded-lg bg-primary/10">
                      <Hash className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Copy as Markdown</div>
                      <div className="text-xs text-muted-foreground">Markdown format</div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadArticle('html')}
                className="gap-2 rounded-xl border-primary/20 text-primary hover:bg-primary/10 hover:border-primary/30 transition-all duration-300 shadow-sm hover:shadow-md px-3 py-2"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with consistent layout */}
      <div className="flex-1 flex overflow-hidden min-w-0">
        {/* Left Side - Article Preview - MAXIMUM WIDTH */}
        <div className="flex-1 min-w-0 overflow-hidden bg-muted/20">
          <ScrollArea className="h-full">
            <div className="w-full h-full pt-4 pr-4 pl-0">
              {/* Article Content Container - Maximum width, no shadow */}
              <div className="h-full bg-white border border-border/50 rounded-lg" style={{ width: '100%' }}>
                <div className="w-full h-full px-8 pt-8 pb-12">
                  {/* Article Title */}
                  {article.title && (
                    <h1 className="text-4xl font-bold text-foreground mb-6 leading-tight">
                      {article.title}
                    </h1>
                  )}
                  
                  {/* Featured Image */}
                  {article.featured_image_url && (
                    <div className="mb-8">
                      <img 
                        src={article.featured_image_url} 
                        alt={article.title || 'Featured image'}
                        className="w-full h-auto rounded-xl shadow-lg object-cover"
                        style={{ maxHeight: '500px' }}
                      />
                    </div>
                  )}
                  
                  {/* Article content with FORCED full width */}
                  <div 
                    className={`${getArticleScopeClass()} w-full`}
                    style={{
                      ...getArticleIsolationStyles(), 
                      width: '100%', 
                      maxWidth: '100%',
                      fontSize: '18px',
                      lineHeight: '1.8',
                      color: 'rgb(51, 51, 51)'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: sanitizeArticleHtml(article.html_content) 
                    }} 
                  />
                </div>
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* Right Side - Details Sidebar with smooth slide-in */}
        <div className="w-80 lg:w-96 border-l bg-gradient-to-br from-muted/30 to-muted/10 overflow-hidden flex flex-col">
            <div className="px-8 py-6 border-b bg-gradient-to-r from-background to-muted/20">
              <div className="flex items-center gap-2 pl-6">
                <div className="p-1.5 rounded-xl bg-primary/10">
                  <Info className="h-4 w-4 text-primary translate-y-[1px]" />
                </div>
                <h3 className="m-0 text-base font-semibold text-foreground leading-none">Article Details</h3>
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-8 space-y-8">
                {/* Content Score */}
                <div className="bg-background/80 border border-border/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 rounded-xl bg-primary/10">
                      <TrendingUp className="h-4 w-4 text-primary translate-y-[1px]" />
                    </div>
                    <span className="text-base font-semibold text-foreground leading-none">Content Score</span>
                  </div>
                  <div className="flex justify-center">
                    <div className="relative w-20 h-20">
                      <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          className="text-primary"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={`${contentScore}, 100`}
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-bold text-foreground">{contentScore}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Article Stats */}
                <div className="bg-background/80 border border-border/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 rounded-xl bg-primary/10">
                      <BarChart3 className="h-4 w-4 text-primary translate-y-[1px]" />
                    </div>
                    <h4 className="m-0 text-base font-semibold text-foreground leading-none">Article Stats</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/30">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-lg bg-primary/10">
                          <Clock className="h-4 w-4 text-primary" />
                        </div>
                        <span className="text-sm font-medium text-foreground">Reading Time</span>
                      </div>
                      <span className="text-sm font-semibold text-foreground">{calculateReadingTime()} min</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/30">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-lg bg-primary/10">
                          <FileText className="h-4 w-4 text-primary" />
                        </div>
                        <span className="text-sm font-medium text-foreground">Word Count</span>
                      </div>
                      <span className="text-sm font-semibold text-foreground">{article.word_count ? article.word_count.toLocaleString() : 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/30">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-lg bg-primary/10">
                          <Image className="h-4 w-4 text-primary" />
                        </div>
                        <span className="text-sm font-medium text-foreground">Images</span>
                      </div>
                      <span className="text-sm font-semibold text-foreground">{article.supporting_images?.length || 0}</span>
                    </div>
                  </div>
                </div>

                {/* SEO Keywords */}
                <div className="bg-background/80 border border-border/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 rounded-xl bg-primary/10">
                      <Target className="h-4 w-4 text-primary translate-y-[1px]" />
                    </div>
                    <h4 className="m-0 text-base font-semibold text-foreground leading-none">Keywords</h4>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-2 block">Primary</label>
                      <Badge variant="secondary" className="text-sm px-3 py-1.5 bg-primary/10 text-primary border-primary/20">
                        {article.primary_keyword || 'N/A'}
                      </Badge>
                    </div>
                    {article.secondary_keywords && article.secondary_keywords.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground mb-2 block">Secondary</label>
                        <div className="flex flex-wrap gap-2">
                          {article.secondary_keywords.map((keyword, index) => (
                            <Badge key={index} variant="outline" className="text-sm px-3 py-1.5 border-border/50">
                              {keyword}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Meta Info */}
                <div className="bg-background/80 border border-border/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 rounded-xl bg-primary/10">
                      <FileText className="h-4 w-4 text-primary translate-y-[1px]" />
                    </div>
                    <h4 className="m-0 text-base font-semibold text-foreground leading-none">Meta Information</h4>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-2 block">Title</label>
                      <div className="p-3 rounded-xl bg-muted/30 border border-border/30">
                        <p className="text-sm text-foreground/90 leading-normal">
                          {article.meta_title || 'N/A'}
                        </p>
                        {article.meta_title && (
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-muted-foreground">
                              {article.meta_title.length}/60 characters
                            </span>
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full transition-all duration-300"
                                style={{ width: `${Math.min((article.meta_title.length / 60) * 100, 100)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </div>
      </div>

      {/* Webflow Queued Dialog */}
      <Dialog open={showWebflowQueuedDialog} onOpenChange={setShowWebflowQueuedDialog}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <img src={PLATFORM_IMAGES.webflow} alt="Webflow" className="h-6 w-6" />
              Article Queued for Publishing
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed pt-2">
              Your article has been successfully added to your Webflow CMS, but it's currently <strong>queued to publish</strong>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">To make your article live:</p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Go to your Webflow Designer</li>
                <li>Open the CMS panel and find your collection</li>
                <li>Click on the article</li>
                <li>Click the status dropdown (top right)</li>
                <li>Select <strong>"Publish now"</strong></li>
              </ol>
            </div>
            
            {publishedUrl && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Article URL:</span>
                <a 
                  href={publishedUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium truncate flex-1"
                >
                  {publishedUrl}
                </a>
              </div>
            )}
            
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-900 dark:text-blue-200">
                <strong>Note:</strong> The article will be visible at this URL once you publish it in Webflow Designer.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={() => setShowWebflowQueuedDialog(false)}
              className="rounded-full w-full"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Website Configuration Dialog */}
      {isAdmin && (
        <Dialog open={showAdminWebsiteDialog} onOpenChange={setShowAdminWebsiteDialog}>
          <DialogContent className="rounded-3xl max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Globe className="h-6 w-6 text-primary" />
                Publish to Platform Blog
              </DialogTitle>
              <DialogDescription className="text-base leading-relaxed pt-2">
                Publish blog posts directly to seoro.so/blog. Articles will be automatically indexed for SEO and visible to search engines.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-sm text-green-900 dark:text-green-200 font-medium mb-2">
                  ✓ Ready to Publish
                </p>
                <p className="text-xs text-green-800 dark:text-green-300">
                  Your articles will be published to <strong>seoro.so/blog</strong> with full SEO optimization, including meta tags, structured data, and automatic sitemap updates.
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-xs text-blue-900 dark:text-blue-200">
                  <strong>Note:</strong> This feature is only available for admin accounts. Published posts will be publicly visible and indexed by search engines.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button 
                onClick={() => setShowAdminWebsiteDialog(false)}
                className="rounded-full w-full"
              >
                Got it
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
